import { useGeo, useGlobalState } from '@/hooks';
import { API_CONFIG } from '@/modules/pmt/constants/affiliate.constants';
import { getAffiliateProducts } from '@/modules/pmt/fetchers/affiliate.fetchers';
import type { AffiliateProduct } from '@/modules/pmt/interfaces';
import { mapAffiliateProducts } from '@/modules/pmt/mappers';
import { getAmazonRegionFromCountry } from '@/modules/pmt/utils/affiliate.utils';
import { useQuery } from '@tanstack/react-query';

export interface UseAffiliateProductsParams {
  shipmentId: string;
}

export const useAffiliateProducts = ({ shipmentId }: UseAffiliateProductsParams) => {
  const { geoInfo } = useGeo();
  const { userInfo, firestoreUuid } = useGlobalState();
  const userUuid = userInfo?.id || firestoreUuid;

  const locale = getAmazonRegionFromCountry(geoInfo.country);

  const {
    data: affiliateProducts,
    isLoading,
    isError,
    refetch,
    isSuccess,
  } = useQuery({
    queryKey: ['affiliate-products', shipmentId],
    queryFn: async () => {
      if (!userUuid) {
        throw new Error('User UUID is required to fetch affiliate products.');
      }
      try {
        const response = await getAffiliateProducts({
          userUuid,
          locale,
          shipmentId,
          page: 1,
          limit: API_CONFIG.MAX_PRODUCTS_TO_SHOW,
        });
        const mappedProducts: AffiliateProduct[] = mapAffiliateProducts(response?.data || []);
        return mappedProducts;
      } catch (error) {
        console.error('Error fetching affiliate products:', error);
        throw error; // Re-throw error for React Query to handle
      }
    },

    // Retry configuration - only for network errors
    retry: (failureCount, error) => {
      // Don't retry if we've reached max attempts
      if (failureCount >= API_CONFIG.MAX_RETRIES) {
        return false;
      }

      // Only retry for network errors, not API errors
      const isNetworkError =
        error.message.includes('fetch') ||
        error.message.includes('network') ||
        error.message.includes('connection') ||
        error.message.includes('timeout');

      return isNetworkError;
    },

    // Exponential backoff for retry delays
    retryDelay: (attemptIndex) =>
      Math.min(
        API_CONFIG.RETRY_DELAY_MS * API_CONFIG.RETRY_BACKOFF_MULTIPLIER ** attemptIndex,
        30000, // Cap at 30 seconds
      ),

    // Additional query options
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid unnecessary API calls
    refetchOnMount: false, // Don't refetch on mount if we have cached data
  });

  return {
    affiliateProducts,
    isLoading,
    isError,
    refetch,
    isSuccess,
  };
};

export default useAffiliateProducts;
